import { api } from '@/lib/api-client'
import type { IssueDerivedStatus, IssueSeverity, IssueStatus } from '@/shared/constants/issue-status'

// ── /sites/{id}/details — HOD-accessible (Slice G, 2026-05-12) ─────────────
//
// Backend widened the existing admin endpoint to HOD-of-site's-dept. Same
// response shape admin gets. Returns site metadata + paginated surveys +
// paginated issues + rollup stats in one call.
//
// We deliberately re-declare the response types here instead of importing
// from `entities/features/site-detail/` — ESLint module boundaries forbid
// cross-system imports. Backend owns the contract; FE consumes it twice.

export interface SiteDetailStats {
  surveys_scheduled: number
  surveys_completed: number
  surveys_missed: number
  issues_active: number
  issues_resolved: number
  survey_progress_percent: number
}

export interface SiteDetailSurveyItem {
  id: string
  survey_ref: string
  status: 'pending' | 'in_progress' | 'completed' | 'missed'
  survey_date?: string | null
  due_date?: string | null
  completed_at?: string | null
  officer_name?: string
  officer?: { id: string; name: string }
  assigned_officer?: { id: string; display_name: string }
  issues_count: number
  has_issues?: boolean
  survey_photos?: string[]
}

export interface SiteDetailIssueItem {
  id: string
  title: string
  severity: IssueSeverity
  status: IssueStatus
  created_at: string
  survey_ref?: string
  section_title?: string
}

export interface SiteDetailInfo {
  id: string
  site_name: string
  name?: string
  address: string
  mandal?: string | null
  contact_number?: string | null
  site_image_url?: string | null
  lat?: number | null
  long?: number | null
  /** Geofence in meters — backend may or may not return on /sites/{id}/details;
   *  we surface it on the hero only when present. */
  geofence_radius_meters?: number | null
  last_survey_date?: string | null
  entity_id: string
  entity_name: string
  department_id?: string | null
  department_name?: string | null
  assigned_officer?: { id: string; display_name: string; contact_number?: string | null } | null
}

interface PaginationMeta {
  total_count: number
  total_pages: number
  current_page: number
  has_next: boolean
  has_previous: boolean
}

interface ApiSiteDetailResponse {
  site: SiteDetailInfo
  assigned_officer?: { id: string; display_name: string; contact_number?: string | null } | null
  last_survey_date?: string | null
  stats: SiteDetailStats
  surveys: { items: SiteDetailSurveyItem[]; total_count: number; page: number; pagination?: PaginationMeta }
  issues: { items: SiteDetailIssueItem[]; total_count: number; page: number; pagination?: PaginationMeta }
}

export interface HodSiteDetail {
  info: SiteDetailInfo
  stats: SiteDetailStats
  surveys: SiteDetailSurveyItem[]
  surveys_total: number
  surveys_page: number
  surveys_has_next: boolean
  issues: SiteDetailIssueItem[]
  issues_total: number
  issues_page: number
  issues_has_next: boolean
}

export interface HodSiteDetailParams {
  surveys_page?: number
  surveys_has_issues?: boolean
  surveys_date_from?: string
  surveys_date_to?: string
  issues_page?: number
  /** DERIVED status (v2): open | pending | overdue | completed | completed-overdue. */
  issues_status?: IssueDerivedStatus
  issues_severity?: IssueSeverity
  issues_date_from?: string
  issues_date_to?: string
}

export function getHodSiteDetail(
  siteId: string,
  params?: HodSiteDetailParams,
): Promise<HodSiteDetail> {
  return api
    .get<ApiSiteDetailResponse>(`/sites/${siteId}/details`, { params })
    .then((d) => {
      const surveysPagination = d.surveys.pagination
      const issuesPagination = d.issues.pagination
      return {
        info: {
          ...d.site,
          assigned_officer: d.assigned_officer ?? d.site.assigned_officer ?? null,
          last_survey_date: d.last_survey_date ?? d.site.last_survey_date ?? null,
        },
        stats: d.stats,
        surveys: d.surveys.items,
        surveys_total: surveysPagination?.total_count ?? d.surveys.total_count ?? d.surveys.items.length,
        surveys_page: surveysPagination?.current_page ?? d.surveys.page ?? 1,
        surveys_has_next: surveysPagination?.has_next ?? false,
        issues: d.issues.items,
        issues_total: issuesPagination?.total_count ?? d.issues.total_count ?? d.issues.items.length,
        issues_page: issuesPagination?.current_page ?? d.issues.page ?? 1,
        issues_has_next: issuesPagination?.has_next ?? false,
      }
    })
}
